import {
  createContext,
  forwardRef,
  useContext,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type HTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../../lib/utils';

/**
 * Tom da mensagem de erro (DEV-259 AC-11/AC-12):
 * - `destructive` (padrão): controles SEM estado de erro próprio (radio,
 *   checkbox, tags, textarea, select, date picker) — só a mensagem sinaliza.
 * - `neutral`: controles que já pintam a própria borda de erro (Input,
 *   Input Group) — a mensagem fica no tom da dica.
 */
export type FormFieldErrorTone = 'destructive' | 'neutral';

/** Textos de apoio que o campo pode ter na tela (um por vez: erro > aviso > dica). */
type FormFieldMessageKind = 'message' | 'warning' | 'hint';

interface FormFieldContextValue {
  id: string;
  error: boolean;
  /** Aviso não bloqueante ativo (DEV-259): substitui a dica e cede lugar ao erro. */
  warning: boolean;
  required: boolean;
  /** Mostra o " (obrigatório)" no label (QA 08/10/2026: alguns grupos do Figma não o têm). */
  requiredSuffix: boolean;
  errorTone: FormFieldErrorTone;
  /**
   * Space-separated ids of the error/warning/hint texts ACTUALLY rendered in
   * this field (EU-761 RN-46). `undefined` when none is — never a dangling id.
   * Wire it as the control's `aria-describedby` when not using
   * `FormFieldControl`.
   */
  describedBy: string | undefined;
  /** Same as `error`; wire it as the control's `aria-invalid`. */
  invalid: boolean;
  /** @internal FormFieldError/Warning/Hint report whether they rendered. */
  registerMessage: (kind: FormFieldMessageKind, mounted: boolean) => void;
}

const FormFieldContext = createContext<FormFieldContextValue>({
  id: '',
  error: false,
  warning: false,
  required: false,
  requiredSuffix: true,
  errorTone: 'destructive',
  describedBy: undefined,
  invalid: false,
  registerMessage: () => {},
});

function useFormField() {
  return useContext(FormFieldContext);
}

/** Tells the field whether a support text is on screen (feeds `describedBy`). */
function useRegisterMessage(kind: FormFieldMessageKind, visible: boolean) {
  const { registerMessage } = useFormField();
  useLayoutEffect(() => {
    if (!visible) return;
    registerMessage(kind, true);
    return () => registerMessage(kind, false);
  }, [kind, visible, registerMessage]);
}

/** Sufixo da convenção de obrigatoriedade (DEV-259 AC-09). Nunca asterisco. */
export const FORM_FIELD_REQUIRED_SUFFIX = ' (obrigatório)';

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  error?: boolean;
  /** Aviso não bloqueante ativo: substitui a dica e cede lugar ao erro. */
  warning?: boolean;
  /** Campo obrigatório: sufixo no label + `aria-required` no controle. */
  required?: boolean;
  /**
   * `false` mantém o `aria-required` e tira só o sufixo do label — para os
   * grupos que o Figma desenha sem "(obrigatório)" (QA 08/10/2026).
   */
  requiredSuffix?: boolean;
  errorTone?: FormFieldErrorTone;
}

function FormField({
  error = false,
  warning = false,
  required = false,
  requiredSuffix = true,
  errorTone = 'destructive',
  className,
  children,
  ...props
}: FormFieldProps) {
  const generatedId = useId();
  const [rendered, setRendered] = useState<Record<FormFieldMessageKind, boolean>>({
    message: false,
    warning: false,
    hint: false,
  });
  const registerMessage = useCallback((kind: FormFieldMessageKind, mounted: boolean) => {
    setRendered((prev) => (prev[kind] === mounted ? prev : { ...prev, [kind]: mounted }));
  }, []);
  const describedBy =
    (['message', 'warning', 'hint'] as const)
      .filter((kind) => rendered[kind])
      .map((kind) => `${generatedId}-${kind}`)
      .join(' ') || undefined;
  const value = useMemo(
    () => ({
      id: generatedId,
      error,
      warning,
      required,
      requiredSuffix,
      errorTone,
      describedBy,
      invalid: error,
      registerMessage,
    }),
    [generatedId, error, warning, required, requiredSuffix, errorTone, describedBy, registerMessage]
  );

  return (
    <FormFieldContext.Provider value={value}>
      <div data-slot="form-field" className={cn('flex flex-col gap-1.5', className)} {...props}>
        {children}
      </div>
    </FormFieldContext.Provider>
  );
}
FormField.displayName = 'FormField';

export interface FormFieldLabelProps extends LabelHTMLAttributes<HTMLLabelElement> {}

const FormFieldLabel = forwardRef<HTMLLabelElement, FormFieldLabelProps>(
  ({ className, htmlFor, children, ...props }, ref) => {
    const { id, error, required, requiredSuffix, errorTone } = useFormField();
    return (
      <label
        ref={ref}
        data-slot="form-field-label"
        htmlFor={htmlFor ?? id}
        className={cn(
          'text-sm font-medium leading-5 text-foreground',
          // Tom neutro (Input/Input Group): o erro é da borda do controle, o
          // label não muda (Figma 20080:1337).
          error && errorTone === 'destructive' && 'text-destructive',
          className
        )}
        {...props}
      >
        {children}
        {required && requiredSuffix ? FORM_FIELD_REQUIRED_SUFFIX : null}
      </label>
    );
  }
);
FormFieldLabel.displayName = 'FormFieldLabel';

export interface FormFieldControlProps {
  /**
   * Render prop with the a11y wiring for the control: `aria-describedby`
   * points at the error, warning or hint text that is actually rendered —
   * `undefined` when none is — `aria-invalid` is `true` while the field has an
   * error, and `aria-required` is set on required fields.
   */
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean;
    'aria-required'?: true;
  }) => ReactNode;
}

function FormFieldControl({ children }: FormFieldControlProps) {
  const { id, error, required, describedBy } = useFormField();
  return (
    <>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error,
        ...(required ? { 'aria-required': true as const } : {}),
      })}
    </>
  );
}
FormFieldControl.displayName = 'FormFieldControl';

export interface FormFieldErrorProps extends HTMLAttributes<HTMLParagraphElement> {}

const FormFieldError = forwardRef<HTMLParagraphElement, FormFieldErrorProps>(
  ({ className, children, ...props }, ref) => {
    const { id, error, errorTone } = useFormField();
    const visible = error && Boolean(children);
    useRegisterMessage('message', visible);
    if (!visible) return null;
    return (
      <p
        ref={ref}
        data-slot="form-field-error"
        id={`${id}-message`}
        aria-live="polite"
        className={cn(
          errorTone === 'neutral'
            ? 'text-sm leading-5 text-muted-foreground'
            : 'text-xs leading-4 text-destructive',
          className
        )}
        {...props}
      >
        {children}
      </p>
    );
  }
);
FormFieldError.displayName = 'FormFieldError';

export interface FormFieldWarningProps extends HTMLAttributes<HTMLParagraphElement> {}

const FormFieldWarning = forwardRef<HTMLParagraphElement, FormFieldWarningProps>(
  ({ className, children, ...props }, ref) => {
    const { id, error, warning } = useFormField();
    const visible = !error && warning && Boolean(children);
    useRegisterMessage('warning', visible);
    if (!visible) return null;
    return (
      <p
        ref={ref}
        data-slot="form-field-warning"
        id={`${id}-warning`}
        aria-live="polite"
        className={cn('text-sm leading-5 text-muted-foreground', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);
FormFieldWarning.displayName = 'FormFieldWarning';

export interface FormFieldHintProps extends HTMLAttributes<HTMLParagraphElement> {}

const FormFieldHint = forwardRef<HTMLParagraphElement, FormFieldHintProps>(
  ({ className, children, ...props }, ref) => {
    const { id, error, warning } = useFormField();
    const visible = !error && !warning && Boolean(children);
    useRegisterMessage('hint', visible);
    if (!visible) return null;
    return (
      <p
        ref={ref}
        data-slot="form-field-hint"
        id={`${id}-hint`}
        className={cn('text-sm leading-5 text-muted-foreground', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);
FormFieldHint.displayName = 'FormFieldHint';

export {
  FormField,
  FormFieldControl,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  FormFieldWarning,
  useFormField,
};
